import { useState } from "react";
import { Heading } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import { CheckboxField } from "../../../../components/common/checkbox-field";
import { AddressBookSection } from "../../../addresses/components/address-book-section";
import { useCheckoutStore } from "../../domain/useCheckoutStore";
import { useUpdateSaleBillingAddress } from "../../hooks/useUpdateSaleBillingAddress";
import { showErrorToast, showSuccessToast } from "../../../../utils/toasts";
import { toCheckoutError } from "../../utils/checkout-error";

interface Props {
  saleId: number;
  /** Called after the new bill-to was saved onto the order. */
  onSaved: () => void;
}

/**
 * Shown on the payment step when paying an existing quotation
 * (`/checkout?saleId=…`) and the BE refused the payment because the order's
 * bill-to can't be used (archived or incomplete). The buyer picks another
 * billing address — or bills the company — and the payment is retried.
 */
export function SaleBillingAddressSection({ saleId, onSaved }: Props) {
  const { setBillingSameAsShipping, setBillingAddressId } = useCheckoutStore();
  const { update, isLoading: isUpdating } = useUpdateSaleBillingAddress();
  const [sameAsShipping, setSameAsShipping] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const handleSave = async () => {
    if (!sameAsShipping && selectedId == null) {
      showErrorToast("Please select or add a billing address.");
      return;
    }
    const billingAddressId = sameAsShipping ? null : selectedId;
    try {
      await update(saleId, billingAddressId);
      // Keep the payment form's picker on what's now on the order — it
      // writes the bill-to again right before charging the card.
      setBillingSameAsShipping(sameAsShipping);
      setBillingAddressId(billingAddressId);
      showSuccessToast("Billing address updated");
      onSaved();
    } catch (err) {
      showErrorToast(toCheckoutError(err, "Could not save the billing address.").message);
    }
  };

  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-borderDefault bg-bgWhite p-5">
      <div className="flex items-center justify-between">
        <Heading variant="h3" tone="dark">Billing address</Heading>
        <CheckboxField
          checked={sameAsShipping}
          onChange={(value) => {
            setSameAsShipping(value);
            if (value) setSelectedId(null);
          }}
          label="Same as delivery address"
          variant="inline"
        />
      </div>
      {!sameAsShipping && (
        <AddressBookSection
          type="invoice"
          heading=""
          selectedId={selectedId}
          onSelect={setSelectedId}
          autoSelectFirst={false}
        />
      )}
      <div className="flex items-center justify-end">
        <Button
          variant="primary"
          size="sm"
          onClick={handleSave}
          disabled={isUpdating || (!sameAsShipping && selectedId == null)}
        >
          {isUpdating ? "Saving…" : "Save"}
        </Button>
      </div>
    </div>
  );
}
